import React, { useState } from 'react';
import { toast } from 'sonner';
import { Pin, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useHighlightPinsQuery } from '@/client/api/highlights/highlights.query';
import { useDeleteHighlightPinMutation } from '@/client/api/highlights/highlights.mutation';
import { HighlightPin, describeSourceRef, HIGHLIGHT_SOURCE_TYPES } from '@/client/types/highlights';
import { HighlightPinDialog } from './HighlightPinDialog';

interface Props {
  etid: number;
}

const fmt = (v: string | null) => (v ? new Date(v).toLocaleDateString() : '—');

const sourceLabel = (t: HighlightPin['source_type']) =>
  HIGHLIGHT_SOURCE_TYPES.find((s) => s.value === t)?.label ?? t;

/** Hand-picked promotions that jump ahead of the automatic cards in this spot. */
export function HighlightPinsPanel({ etid }: Props) {
  const { data: pins, isLoading } = useHighlightPinsQuery(etid);
  const del = useDeleteHighlightPinMutation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDelete = async (pin: HighlightPin) => {
    if (!window.confirm('Stop featuring this pick here? It goes back to showing automatically.')) return;
    try {
      await del.mutateAsync({ hpid: pin.hpid, etid });
      toast.success('Removed');
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not remove');
    }
  };

  const rows = [...(pins ?? [])].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));

  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 p-4">
        <div className="flex items-center gap-2">
          <Pin className="h-4 w-4 text-blue-700" />
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Featured picks</h2>
            <p className="text-xs text-gray-500">Shown first, in this order, ahead of the automatic cards.</p>
          </div>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Feature something
        </Button>
      </div>

      <ol className="divide-y divide-gray-100">
        {isLoading &&
          Array.from({ length: 2 }).map((_, i) => (
            <li key={i} className="p-4">
              <Skeleton className="h-5 w-2/3" />
            </li>
          ))}

        {!isLoading && rows.length === 0 && (
          <li className="px-4 py-8 text-center text-sm text-gray-500">
            Nothing featured yet — this spot fills itself with the latest matching listings.
            <br />
            Use <span className="font-medium text-gray-700">Feature something</span> to hand-pick what shows first.
          </li>
        )}

        {!isLoading &&
          rows.map((pin, i) => (
            <li key={pin.hpid} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{sourceLabel(pin.source_type)}</p>
                <p className="truncate text-xs text-gray-500">
                  <span className="font-mono">{describeSourceRef(pin)}</span>
                  {' · '}
                  {pin.start_at || pin.end_at ? `${fmt(pin.start_at)} → ${fmt(pin.end_at)}` : 'Always visible'}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="shrink-0 text-red-600"
                onClick={() => handleDelete(pin)}
                disabled={del.isPending}
                aria-label="Remove featured pick"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
      </ol>

      <HighlightPinDialog etid={etid} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
